import React, { ReactElement, useMemo } from 'react';
import { Switch, Route, useRouteMatch, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { v5 } from 'uuid';
import { Helmet } from 'react-helmet';
import { useGetGiftMetaQuery } from '@alycecom/services';
import { skipToken } from '@reduxjs/toolkit/query';

import useAcceptPolicy from '../../../hooks/useAcceptPolicy';
import useInitialDataLoading from '../hooks/useInitialDataLoading';
import NoMatchRoute from '../../../components/Shared/NoMatchRoute';
import useRestartGiftFlow from '../hooks/useRestartGiftFlow';
import useTrackGiftAccess from '../hooks/useTrackGiftAccess';
import { getIsFulfilled, getRecipientState } from '../../../store/users/users.selectors';
import { getIsLoaded, getStatuses } from '../store/statuses/statuses.selectors';
import { getIsSegmentLoaded } from '../../../store/globalSettings/globalSettings.selectors';
import { useInitSegments } from '../../../hooks/useInitSegments';
import { metaDescription, metaTitle } from '../../../constants/meta.constants';

import AcceptPage from './AcceptPage/AcceptPage';
import DeclinePage from './DeclinePage/DeclinePage';
import ThankYouPage from './ThankYouPage/ThankYouPage';
import MarketplacePage from './MarketplacePage/MarketplacePage';
import NeedMoreInfoPage from './NeedMoreInfoPage/NeedMoreInfoPage';
import LandingPage from './LandingPage/LandingPage';

const RecipientModule = (): ReactElement => {
  const { hashId } = useParams<{ hashId: string }>();
  const { url: parentUrl } = useRouteMatch();

  const isUsersFulfilled = useSelector(getIsFulfilled);
  const isStatusesLoaded = useSelector(getIsLoaded);
  const isSegmentLoaded = useSelector(getIsSegmentLoaded);
  const recipient = useSelector(getRecipientState);
  const { preview } = useSelector(getStatuses);

  const isDataReady = isUsersFulfilled && isSegmentLoaded && isStatusesLoaded && !preview;
  const identityPayload = useMemo(() => ({ id: v5(recipient?.email ?? '', v5.DNS) }), [recipient]);
  const initSegmentsParams = useMemo(() => ({ isDataReady, payload: identityPayload }), [isDataReady, identityPayload]);

  useInitialDataLoading();
  useInitSegments(initSegmentsParams);
  useTrackGiftAccess(hashId);
  useRestartGiftFlow();

  const handleAcceptPolicy = useAcceptPolicy();

  const { data } = useGetGiftMetaQuery(hashId || skipToken);
  const { header = metaTitle, description = metaDescription } = data || {};

  return (
    <>
      <Helmet>
        <title>{header}</title>
        <meta name="description" content={description} />
        <meta name="title" property="og:title" content={header} />
        <meta name="description" property="og:description" content={description} />
      </Helmet>
      <Switch>
        <Route path={parentUrl} exact render={() => <LandingPage onAcceptPolicy={handleAcceptPolicy} />} />
        <Route path={`${parentUrl}/accept`} render={() => <AcceptPage parentUrl={parentUrl} />} />
        <Route path={`${parentUrl}/decline`} exact render={() => <DeclinePage parentUrl={parentUrl} />} />
        <Route path={`${parentUrl}/thank-you`} exact component={ThankYouPage} />
        <Route path={`${parentUrl}/finish-info`} exact component={NeedMoreInfoPage} />
        <Route path={`${parentUrl}/marketplace`} render={() => <MarketplacePage parentUrl={parentUrl} />} />
        <Route path="*" component={NoMatchRoute} />
      </Switch>
    </>
  );
};

export default RecipientModule;
